import { create } from 'zustand';

import { colorForKey } from '@utils/objectColor';

/**
 * Wizard step order for the AI-detection Object Picker.
 * The modal walks the user through three steps:
 *   0 - `collection`: choose the saved collection to detect
 *   1 - `area`:       choose where to run detection (draw an AOI or entire project)
 *   2 - `run`:        confirm and start detection
 */
export const PICKER_STEPS = ['collection', 'area', 'run'];

/**
 * Store for the AI-detection Object Picker.
 *
 * Holds:
 * - `isOpen`: whether the ObjectPickerModal is visible.
 * - the chosen collection: `sourceCollectionId` (what the run is sent for),
 *   `sourceCollectionName` and `selectedObjects` (`{ key, label, color }`, for
 *   display on the Area and Run steps).
 */
export const useObjectPickerStore = create(set => ({
	// Modal open/close state
	isOpen: false,
	open: () => set({ isOpen: true }),
	// Hides the modal without resetting state.
	close: () => set({ isOpen: false, isDrawing: false }),
	// Hides the modal and fully resets wizard + selection state so the next open
	// starts fresh. Job-tracking fields (activeJobUuid, activeJobProgressModal)
	// are intentionally preserved — they track a background process that outlives
	// the modal.
	closeAndReset: () =>
		set({
			isOpen: false,
			isDrawing: false,
			// Wizard state
			step: 0,
			areaMode: null,
			aoiPolygon: null,
			detectionPreview: null,
			// Selection state
			selectedObjects: [],
			sourceCollectionId: null,
			sourceCollectionName: '',
		}),
	setOpen: isOpen => set({ isOpen }),
	toggleOpen: () => set(state => ({ isOpen: !state.isOpen })),

	// Wizard step (index into PICKER_STEPS)
	step: 0,
	setStep: step => set({ step }),
	nextStep: () => set(state => ({ step: Math.min(state.step + 1, PICKER_STEPS.length - 1) })),
	// Stepping back abandons the pending confirmation: the user is on their way
	// to change what would run, so the preview no longer describes it.
	prevStep: () => set(state => ({ step: Math.max(state.step - 1, 0), detectionPreview: null })),

	// Hides the modal while the user sketches an AOI on the map.
	isDrawing: false,
	setDrawing: isDrawing => set({ isDrawing }),

	// The detection job kicked off by a run. Drives the (dependent) progress
	// modal shown after the picker closes; `null` when nothing is running/tracked.
	activeJobUuid: null,
	setActiveJobUuid: activeJobUuid => set({ activeJobUuid }),
	activeJobProgressModal: false,
	setActiveJobProgressModal: activeJobProgressModal => set({ activeJobProgressModal }),

	// Area scoping for the detection run. Both setters drop any detection preview:
	// a preview describes a specific collection + area, so changing the area makes
	// the stored outcome (and its scan-area figure) wrong.
	areaMode: null,
	setAreaMode: areaMode => set({ areaMode, detectionPreview: null }),
	aoiPolygon: null,
	setAoiPolygon: aoiPolygon => set({ aoiPolygon, detectionPreview: null }),

	// The un-confirmed result of a detect request (`confirm` omitted): the
	// server's verdict on what a run would do — see `@utils/detectionOutcome`.
	// Set when the Area step previews, and read by the Run step to show the ground
	// a run would scan. Lives here rather than inside `useRunDetection` because
	// the hook's single instance is owned by the modal shell, while the step that
	// renders it is a separate component. `null` means nothing previewed yet.
	detectionPreview: null,
	setDetectionPreview: detectionPreview => set({ detectionPreview }),

	// The chosen collection. `null` until one is selected on the Collection step.
	sourceCollectionId: null,
	// Display-only name of the chosen collection, shown on the Area and Run steps.
	sourceCollectionName: '',
	// The chosen collection's objects — each `{ key, label, color }`, `key` being the node uuid.
	selectedObjects: [],

	/**
	 * Choose the collection to detect. The area is kept — it describes the
	 * ground, not the objects — but a preview describes the previously chosen
	 * collection, so it is dropped.
	 */
	selectCollection: collection =>
		set({
			detectionPreview: null,
			sourceCollectionId: collection?.id ?? null,
			sourceCollectionName: collection?.name ?? '',
			selectedObjects: (collection?.objects ?? []).map(object => ({
				key: object.key,
				label: object.label,
				// Same fallback as the collection card, so an object with no stored
				// color keeps its swatch on the Area and Run steps.
				color: object.color ?? colorForKey(object.key),
			})),
		}),
}));
